import { createSlice, createAction, PayloadAction } from "@reduxjs/toolkit";
import { errorMessageForResetPassword } from "@/common/classes/UserUtils";

// Trigger actions for saga compatibility (sagas watch these)
export const refreshCurrentUserData = createAction<any>(
  "auth/refreshCurrentUserData"
);
export const initializeCurrentUserData = createAction<any>(
  "auth/initializeCurrentUserData"
);
export const attemptLogout = createAction<any>("auth/attemptLogout");
export const attemptLogin = createAction<any>("auth/attemptLogin");
export const attemptRegister = createAction<any>("auth/attemptRegister");
export const resetPassword = createAction<any>("auth/resetPassword");

// These must keep their original action type strings because other slices
// listen for them in their extraReducers (e.g., organizationTeam resets on LOGOUT_START).
export const logoutStart = createAction<any>("LOGOUT_START");
export const logoutDone = createAction<any>("LOGOUT_DONE");

// External action from account_activation saga
const deactivateAccountSuccess = createAction("DEACTIVATE_ACCOUNT_SUCCESS");

interface CurrentUserState {
  bootstrapped: boolean;
  loading: boolean;
  loaded: boolean;
  isAuthenticated: boolean;
  isVerified: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  errors: string[];
  data: Record<string, any>;
  email?: string;
}

interface ResetPasswordState {
  loading: boolean;
  errors: string[];
  message: string;
  succeeded: boolean;
  failed: boolean;
}

interface AuthState {
  currentUser: CurrentUserState;
  resetPassword: ResetPasswordState;
}

const initialState: AuthState = {
  currentUser: {
    bootstrapped: false,
    loading: false,
    loaded: false,
    isAuthenticated: false,
    isVerified: false,
    isAdmin: false,
    isStaff: false,
    errors: [],
    data: {},
  },
  resetPassword: {
    loading: false,
    errors: [],
    message: "",
    succeeded: false,
    failed: false,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // --- Refresh current user data ---
    refreshCurrentUserDataStart(state, _action: PayloadAction<any>) {
      // Note: we omit loading/loaded here since the user should
      // already be bootstrapped. This prevents unnecessary
      // disruption to the UI.
    },
    refreshCurrentUserDataSuccess(state, action: PayloadAction<any>) {
      state.currentUser.data = action.payload;
      state.currentUser.isAuthenticated = Boolean(action.payload.id);
      state.currentUser.isVerified = Boolean(
        action.payload.email_verified_at
      );
      state.currentUser.errors = [];
    },
    refreshCurrentUserDataFailure(state, action: PayloadAction<any>) {
      state.currentUser.errors = action.payload.errors;
    },

    // --- Initialize current user data ---
    initializeCurrentUserDataStart(state, _action: PayloadAction<any>) {
      state.currentUser.bootstrapped = false;
      state.currentUser.data = {};
      state.currentUser.errors = [];
      state.currentUser.isAdmin = false;
      state.currentUser.isStaff = false;
      state.currentUser.loading = true;
      state.currentUser.loaded = false;
    },
    initializeCurrentUserDataSuccess(state, action: PayloadAction<any>) {
      state.currentUser.bootstrapped = true;
      state.currentUser.data = action.payload;
      state.currentUser.errors = [];
      state.currentUser.isAdmin =
        action.payload.system_role_machine_name === "admin";
      state.currentUser.isStaff =
        action.payload.system_role_machine_name === "staff";
      state.currentUser.isAuthenticated = Boolean(action.payload.id);
      state.currentUser.isVerified = Boolean(
        action.payload.email_verified_at
      );
      state.currentUser.loaded = true;
      state.currentUser.loading = false;
    },
    initializeCurrentUserDataFailure(state, action: PayloadAction<any>) {
      state.currentUser.bootstrapped = true;
      state.currentUser.errors = action.payload.errors;
      state.currentUser.loading = false;
    },

    // --- Registration ---
    registerInit(state) {
      state.currentUser.errors = [];
    },
    registerStart(state, _action: PayloadAction<any>) {
      state.currentUser.loading = true;
      state.currentUser.errors = [];
    },
    registerSuccess(state, _action: PayloadAction<any>) {
      state.currentUser.isAuthenticated = true;
      state.currentUser.loading = false;
      state.currentUser.errors = [];
    },
    registerFailure(state, action: PayloadAction<any>) {
      state.currentUser.loading = false;
      state.currentUser.errors = action.payload.errors;
    },

    // --- Login ---
    loginStart(state, _action: PayloadAction<any>) {
      state.currentUser.loading = true;
      state.currentUser.errors = [];
    },
    loginSuccess(state, action: PayloadAction<any>) {
      state.currentUser.isAuthenticated = true;
      state.currentUser.isVerified = Boolean(
        action.payload.email_verified_at
      );
      state.currentUser.email = action.payload.email;
      state.currentUser.errors = [];
    },
    loginFailure(state, _action: PayloadAction<any>) {
      state.currentUser.loading = false;
      state.currentUser.errors = ["Log in credentials are invalid"];
    },

    // --- Reset password ---
    resetPasswordStart(state, _action: PayloadAction<any>) {
      state.resetPassword.loading = true;
      state.resetPassword.succeeded = false;
      state.resetPassword.failed = false;
    },
    resetPasswordSuccess(state, _action: PayloadAction<any>) {
      state.resetPassword.loading = false;
      state.resetPassword.message = "Password successfully reset!";
      state.resetPassword.succeeded = true;
      state.resetPassword.failed = false;
    },
    resetPasswordFailure(state, action: PayloadAction<any>) {
      state.resetPassword.loading = false;
      state.resetPassword.errors = [
        errorMessageForResetPassword(action.payload),
      ];
      state.resetPassword.succeeded = false;
      state.resetPassword.failed = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logoutStart, (_state) => {
        // no-op for auth state, state passes through
        // (logoutStart is dispatched before the actual logout request)
      })
      .addCase(logoutDone, (state) => {
        state.currentUser.isAuthenticated = false;
        state.currentUser.isVerified = false;
        state.currentUser.isAdmin = false;
        state.currentUser.isStaff = false;
        state.currentUser.errors = [];
        state.currentUser.data = {};
      })
      .addCase(deactivateAccountSuccess, (state) => {
        state.currentUser.isAuthenticated = false;
        state.currentUser.isVerified = false;
        state.currentUser.isAdmin = false;
        state.currentUser.isStaff = false;
        state.currentUser.data = {};
      });
  },
});

export const {
  refreshCurrentUserDataStart,
  refreshCurrentUserDataSuccess,
  refreshCurrentUserDataFailure,
  initializeCurrentUserDataStart,
  initializeCurrentUserDataSuccess,
  initializeCurrentUserDataFailure,
  registerInit,
  registerStart,
  registerSuccess,
  registerFailure,
  loginStart,
  loginSuccess,
  loginFailure,
  resetPasswordStart,
  resetPasswordSuccess,
  resetPasswordFailure,
} = authSlice.actions;

export default authSlice.reducer;
